import React, { useEffect } from 'react';
import { useScreenContext } from '../../../context/ScreenContext/hook';
import { BaseModal } from './components/BaseModal';
import type { ModalCloseClickLocation } from './components/consts';
import { ModalBackdrop } from './components/ModalBackdrop';
import { Portal } from './components/Portal';
import { ModalProps, ModalType } from './types';
import { useModalKeyboard } from './useModalKeyboard';

export const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  children,
  modalType = ModalType.STANDARD,
  isShowing,
  onClose,
  preventExternalClose = false,
  ariaLabel,
  onOutsideClick,
}) => {
  const { isLargeScreen } = useScreenContext();
  const hideOverlay = (modalType === ModalType.DOCKED && !isLargeScreen) || modalType === ModalType.FULL_SCREEN;
  const container = document.getElementById('root') ?? undefined;

  useEffect(() => {
    if (isShowing) {
      // Prevent rubber-band/overscroll on iOS and hide page scroll
      document.documentElement.classList.add('overscroll-none');
      document.body.classList.add('overflow-hidden');
    } else if (!isShowing) {
      // Delay removal until after the BaseModal exit animation (150ms) completes.
      // Removing overflow-hidden while the modal is still animating out causes
      // iOS Safari to fail to repaint the safe area regions, leaving them transparent.
      const timer = setTimeout(() => {
        document.documentElement.classList.remove('overscroll-none');
        document.body.classList.remove('overflow-hidden');
        // Force Safari to repaint the viewport after scroll unlock.
        // Without this, the top safe area can remain transparent.
        // Toggling a class forces Safari to recomposite the safe area regions.
        document.body.classList.add('transform-gpu');
        requestAnimationFrame(() => {
          document.body.classList.remove('transform-gpu');
        });
      }, 150);
      return () => clearTimeout(timer);
    }

    return () => {
      document.documentElement.classList.remove('overscroll-none');
      document.body.classList.remove('overflow-hidden');
    };
  }, [isShowing, container]);

  // As a safety net across routers/containers, intercept scroll gestures globally while modal is open
  useEffect(() => {
    if (!isShowing) return;

    const hasScrollableAncestor = (el: Element, boundary: Element) => {
      const canScroll = (node: Element) => {
        const cs = window.getComputedStyle(node);
        const oy = cs.overflowY;
        return (oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight;
      };
      // Iteratively traverse ancestors with early exit to avoid allocations in hot paths (for-loop allowed by lint cfg)
      for (
        let node: Element | null = el;
        node && node !== boundary && node !== document.body && node !== document.documentElement;
        node = node.parentElement
      ) {
        if (canScroll(node)) return true;
      }
      // Check the boundary element as well
      return canScroll(boundary);
    };

    const shouldBlock = (e: Event) => {
      const { target } = e;
      if (!(target instanceof Element)) return true;
      const modalRoot = target.closest('#modal');
      if (!modalRoot) return true; // outside modal => block
      // Prefer composedPath for accurate ancestry across shadow DOM; fallback to iterative traversal
      const path = e.composedPath?.();
      if (path) {
        for (let i = 0; i < path.length; i++) {
          const n = path[i];
          if (!(n instanceof Element)) continue;
          if (n === modalRoot || n === document.body || n === document.documentElement) break;
          const cs = window.getComputedStyle(n);
          const oy = cs.overflowY;
          if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) {
            return false; // allow event (do not block)
          }
        }
        const cs = window.getComputedStyle(modalRoot);
        const oy = cs.overflowY;
        const modalCanScroll = (oy === 'auto' || oy === 'scroll') && modalRoot.scrollHeight > modalRoot.clientHeight;
        return !modalCanScroll;
      }
      // Fallback
      return !hasScrollableAncestor(target, modalRoot);
    };

    const onWheel = (e: WheelEvent) => {
      if (shouldBlock(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (shouldBlock(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });

    return () => {
      window.removeEventListener('wheel', onWheel, true);
      window.removeEventListener('touchmove', onTouchMove, true);
    };
  }, [isShowing]);

  const handleExternalClose = (clickLocation: ModalCloseClickLocation) => {
    if (preventExternalClose) {
      return;
    }
    if (onOutsideClick) {
      onOutsideClick(clickLocation);
      return;
    }
    onClose?.(clickLocation);
  };

  const { dialogRef, onDialogKeyDown } = useModalKeyboard({
    isShowing,
    onEscape: () => handleExternalClose('escape-key'),
  });

  return (
    <Portal>
      <ModalBackdrop onBackdropClick={handleExternalClose} isShowing={isShowing && !hideOverlay} />
      <BaseModal
        onBackdropClick={() => handleExternalClose('backdrop-click')}
        modalType={modalType}
        isShowing={isShowing}
        ariaLabel={ariaLabel}
        dialogRef={dialogRef}
        onDialogKeyDown={onDialogKeyDown}>
        {children}
      </BaseModal>
    </Portal>
  );
};
